/**
 * Client-side implementation of dynamic().
 *
 * Resolved via package.json conditional exports for browser builds (default condition).
 * Components load asynchronously via React.lazy + Suspense, and may be preloaded
 * ahead of time via each component's `.preload()` helper.
 */
import type {ComponentPropsWithRef, ComponentType, FC} from 'react'
import {Suspense} from 'react'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {ComponentResource} from './component-resource'
import type {DynamicClientOnlyOptions, DynamicComponent, DynamicOptions} from './dynamic-types'

// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {DynamicOptions, DynamicComponent, DynamicClientOnlyOptions} from './dynamic-types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function dynamic<T extends ComponentType<any>>(
  load: () => Promise<{default: T}>,
  options: DynamicOptions = {},
): DynamicComponent<T> {
  if (options.ssr === false) {
    return dynamicClientOnly(load, {loading: options.loading ?? null, displayName: options.displayName})
  }

  const resource = new ComponentResource(load)
  const name = options.displayName
  const displayName = name ? `DYNAMIC(${name})` : 'DYNAMIC'

  const DynamicComponent: FC<ComponentPropsWithRef<T>> = props => {
    // Sync fast-path: if this dynamic component was preloaded and already resolved,
    // render it directly without going through React.lazy's Suspense.
    const Resolved = resource.getResolved()

    if (Resolved) return <Resolved {...props} />

    // Component hasn't resolved yet — delegate to React.lazy.
    // Callers must provide their own <Suspense> boundary; we intentionally
    // do NOT wrap here so loading UI is controlled by the consumer.
    return <resource.Lazy {...props} />
  }

  return Object.assign(DynamicComponent, {
    preload: () => resource.preload(),
    ssr: true,
    Loading: null,
    displayName,
  }) as DynamicComponent<T>
}

/**
 * Client-side dynamicClientOnly: loads and renders via React.lazy + Suspense.
 * Shows the loading fallback until the component resolves.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dynamicClientOnly<T extends ComponentType<any>>(
  load: () => Promise<{default: T}>,
  options: DynamicClientOnlyOptions = {},
): DynamicComponent<T> {
  const {loading: Loading = null, displayName: name} = options
  const displayName = name ? `DYNAMIC-CLIENT(${name})` : 'DYNAMIC-CLIENT'

  const resource = new ComponentResource(load)

  const ClientOnly: FC<ComponentPropsWithRef<T>> = props => {
    const fallback = Loading ? <Loading /> : null
    const {isServer} = useRuntimeEnvironment()

    // During SSR (renderToString), render the fallback directly so hydration matches.
    if (isServer) return <>{fallback}</>

    return (
      <Suspense fallback={fallback}>
        <resource.Lazy {...props} />
      </Suspense>
    )
  }

  return Object.assign(ClientOnly, {
    preload: () => resource.preload(),
    ssr: false,
    Loading,
    displayName,
  }) as DynamicComponent<T>
}
